import * as R from "ramda";

import { createHandlers } from "redux-mvc";

import { MAX_SPEAKERS, NAMESPACE } from "Portal/CallForPapersForm/constants";
import * as STANDARD_MODULE_FIELD_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-module-field-ids";
import moment from "moment";

const iniState = {
  submissionId: null,
  closeAt: null,
  isClosed: false,
  loading: false,
  saving: false,
  verifyingEmail: false,
  searching: false,
  searchingSpeakerEmail: false,
  sessionId: null,
  error: null,
  emailValidated: null,
  userValidated: false,
  userExists: false,
  portalUserExists: false,
  isVirtualUser: false,
  shouldEnforceSso: false,
  disableSignup: false,
  customMessage: null,
  activeIndex: 0,
  formData: {},
  eventTimezone: null,
  eventEnableExternalUsers: true,
  eventEnableUniqueContacts: false,
  termsAccepted: false,
  groupValues: {},
  contactValues: {},
  loginValues: {},
  formId: "",
  userFirstName: null,
  userLastName: null,
  isPasswordValid: false,
  selectedAccountId: null,
  selectedContactId: null,
  availableGroups: [],
  userEmail: null,
  isLoggedIn: false,
  loggedInLabel: null,
  loggedInFirstName: null,
  eventUserGroups: [],
  selectedSpeakerId: 1,
  isViewOnly: false,
  speakersLimit: MAX_SPEAKERS,
  speakers: [
    {
      id: 1,
      is_new: true,
      is_self: false,
      is_external: false,
      emailError: "",
      contactValues: {},
      additionalContacts: []
    }
  ],
  singleSignOnIdentityProviders: [],
  emailError: "",
  showConfirmation: false,
  sections: [],
  wannaCompleteAndIsInvalid: false,
  styles: {},
  //
  enableEventSubmissionLimit: false,
  enableSubmissionLimit: false,
  eventSubmissionLimit: 0,
  submissionLimit: 0,
  totalEventSubmissionForUser: 0,
  totalFormSubmissionForUser: 0,
  userCanSubmitNewSubmissions: true,
  enableAdditionalContacts: true
};

const model = createHandlers({
  iniState,
  reducers: {
    init: R.identity,
    nextPage: R.identity,
    backPage: R.identity,
    goToLoginPage: R.identity,
    goToFormPage: R.identity,
    login: R.identity,
    saveValues: R.identity,
    handleCallback: R.identity,
    handleLogin: R.identity,
    handleLogout: R.identity,
    checkUser: R.identity,
    onLogin: R.identity,
    validateLogin: R.identity,
    onCreateAccount: R.identity,
    verifySpeakerEmail: R.identity,
    setUserData: (
      _,
      {
        payload: {
          userExists,
          isVirtualUser,
          portalUserExists,
          shouldEnforceSso,
          disableSignup,
          customMessage,
          userValidated,
          emailValidated,
          error,
          isPasswordValid
        }
      }
    ) => ({
      userExists,
      isVirtualUser,
      portalUserExists,
      shouldEnforceSso,
      disableSignup,
      customMessage,
      userValidated,
      emailValidated,
      error,
      isPasswordValid
    }),
    seedFormData: (
      state,
      {
        payload: {
          submissionId,
          userEmail,
          formPayload,
          eventUserGroups,
          isLoggedIn,
          loggedInLabel,
          loggedInFirstName,
          contactValues,
          speakers,
          groupValues,
          speakersLimit,
          sections
        }
      }
    ) => {
      const speakersToUse =
        speakers && speakers.length ? speakers : state.speakers;

      return {
        submissionId,
        activeIndex: submissionId ? 2 : 0,
        userEmail: userEmail ? userEmail.toLowerCase().trim() : userEmail,
        formData: formPayload,
        closeAt: formPayload.close_at,
        isClosed: formPayload.is_closed,
        availableGroups: formPayload.available_accounts || [],
        eventUserGroups: eventUserGroups || [],
        isLoggedIn,
        loggedInLabel,
        loggedInFirstName,
        eventTimezone: formPayload.event_timezone ?? null,
        eventEnableUniqueContacts: formPayload.event_enable_unique_contacts,
        eventEnableExternalUsers: formPayload.event_enable_external_users,
        selectedAccountId: null,
        selectedContactId: null,
        contactValues: contactValues
          ? contactValues
          : formPayload.selected_contact_values || {},
        groupValues: groupValues
          ? groupValues
          : formPayload.selected_account_values || {},
        speakers: speakersToUse,
        selectedSpeakerId: speakersToUse[0].id,
        isViewOnly: !formPayload.is_editable,
        speakersLimit: speakersLimit || MAX_SPEAKERS,
        sections,
        wannaCompleteAndIsInvalid: false,
        styles: formPayload.styles,
        //
        enableEventSubmissionLimit: formPayload.enable_event_submission_limit,
        enableSubmissionLimit: formPayload.enable_submission_limit,
        eventSubmissionLimit: formPayload.event_submission_limit,
        submissionLimit: formPayload.submission_limit,
        totalEventSubmissionForUser:
          formPayload.total_event_submissions_for_user,
        totalFormSubmissionForUser: formPayload.total_form_submissions_for_user,
        userCanSubmitNewSubmissions:
          formPayload.user_can_submit_new_submissions,
        enableAdditionalContacts: formPayload.enable_additional_contacts
      };
    },
    toggleTermsAccepted: state => ({
      termsAccepted: !state.termsAccepted
    }),
    updateGroupValues: (state, { payload: { id, value } }) => {
      // handle: starts at
      if (id === STANDARD_MODULE_FIELD_IDS.SESSIONS.STARTS_AT) {
        const startsAt = R.path(["value", "iso"])(value);
        if (!startsAt) {
          return {
            groupValues: R.assoc(
              id,
              {
                type: "datetime",
                value: { iso: null }
              },
              state.groupValues
            )
          };
        }
        const endsAt = R.path([
          "groupValues",
          STANDARD_MODULE_FIELD_IDS.SESSIONS.ENDS_AT,
          "value",
          "iso"
        ])(state);
        if (endsAt) {
          const start = moment(startsAt);
          const end = moment(endsAt);
          if (start.isAfter(end)) {
            return {
              groupValues: {
                ...state.groupValues,
                [STANDARD_MODULE_FIELD_IDS.SESSIONS.STARTS_AT]: value,
                [STANDARD_MODULE_FIELD_IDS.SESSIONS.ENDS_AT]: {
                  type: "datetime",
                  value: {
                    iso: start
                      .clone()
                      .add(1, "hours")
                      .utc()
                      .format()
                  }
                }
              }
            };
          }
        }
      }

      // handle: ends at
      if (id === STANDARD_MODULE_FIELD_IDS.SESSIONS.ENDS_AT) {
        const endsAt = R.path(["value", "iso"])(value);
        if (!endsAt) {
          return {
            groupValues: R.assoc(
              id,
              {
                type: "datetime",
                value: { iso: null }
              },
              state.groupValues
            )
          };
        }
        const startsAt = R.path([
          "groupValues",
          STANDARD_MODULE_FIELD_IDS.SESSIONS.STARTS_AT,
          "value",
          "iso"
        ])(state);
        if (startsAt) {
          const start = moment(startsAt);
          const end = moment(endsAt);
          if (start.isAfter(end)) {
            return {
              groupValues: {
                ...state.groupValues,
                [STANDARD_MODULE_FIELD_IDS.SESSIONS.ENDS_AT]: value,
                [STANDARD_MODULE_FIELD_IDS.SESSIONS.STARTS_AT]: {
                  type: "datetime",
                  value: {
                    iso: end
                      .clone()
                      .subtract(1, "hours")
                      .utc()
                      .format()
                  }
                }
              }
            };
          }
        }
      }

      return {
        groupValues: R.assoc(id, value, state.groupValues)
      };
    },
    updateContactValues: (state, { payload: { id, value } }) => ({
      contactValues: R.assoc(id, value, state.contactValues)
    }),
    clearExternalSpeaker: (state, { payload: selectedSpeakerId }) => {
      const newSpeakerId = Math.random();
      return {
        selectedSpeakerId: newSpeakerId,
        speakers: R.map(speaker => {
          if (speaker.id === selectedSpeakerId) {
            return {
              id: newSpeakerId,
              is_new: true,
              is_self: false,
              is_external: false,
              emailError: "",
              contactValues: {},
              additionalContacts: []
            };
          }
          return speaker;
        })(state.speakers)
      };
    },
    updateSpeakerEmailError: (
      state,
      { payload: { selectedSpeakerId, emailError } }
    ) => ({
      speakers: R.map(speaker => {
        if (speaker.id === selectedSpeakerId) {
          return {
            ...speaker,
            emailError
          };
        }
        return speaker;
      })(state.speakers)
    }),
    setSpeakerAsExternal: (state, { payload: { selectedSpeakerId } }) => ({
      speakers: R.map(speaker => {
        if (speaker.id === selectedSpeakerId) {
          return {
            ...speaker,
            is_external: true
          };
        }
        return speaker;
      })(state.speakers)
    }),
    clearSpeakersEmail: (state, { payload: { selectedSpeakerId } }) => ({
      speakers: R.map(speaker => {
        if (speaker.id === selectedSpeakerId) {
          return {
            ...speaker,
            contactValues: {
              ...speaker.contactValues,
              [STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL]: null
            }
          };
        }
        return speaker;
      })(state.speakers)
    }),
    updateSpeakerContactValues: (
      state,
      { payload: { selectedSpeakerId, id, value } }
    ) => {
      return {
        speakers: R.map(speaker => {
          if (speaker.id === selectedSpeakerId) {
            return {
              ...speaker,
              contactValues: R.assoc(id, value, speaker.contactValues)
            };
          }
          return speaker;
        })(state.speakers)
      };
    },
    updateSpeakerAdditionalContact: (
      state,
      { payload: { selectedSpeakerId, contactIndex, contactValues } }
    ) => {
      return {
        speakers: R.map(speaker => {
          if (speaker.id === selectedSpeakerId) {
            const additionalContacts = speaker.additionalContacts;
            additionalContacts[contactIndex] = contactValues;
            return {
              ...speaker,
              additionalContacts
            };
          }
          return speaker;
        })(state.speakers)
      };
    },
    removeSpeakerAdditionalContact: (
      state,
      { payload: { selectedSpeakerId, contactIndex } }
    ) => {
      const speakers = state.speakers.map(speaker => {
        if (speaker.id === selectedSpeakerId) {
          const additionalContacts = [...speaker.additionalContacts];
          additionalContacts.splice(contactIndex, 1);
          return {
            ...speaker,
            additionalContacts
          };
        } else {
          return speaker;
        }
      });
      return {
        speakers
      };
    },
    addSpeakerAdditionalContact: (
      state,
      { payload: { selectedSpeakerId } }
    ) => {
      return {
        speakers: R.map(speaker => {
          if (speaker.id === selectedSpeakerId) {
            const additionalContacts = speaker.additionalContacts;
            additionalContacts.push({
              id: null,
              firstName: "",
              lastName: "",
              email: "",
              role: ""
            });
            return {
              ...speaker,
              additionalContacts
            };
          }
          return speaker;
        })(state.speakers)
      };
    },
    updateLoginValues: (state, { payload: { id, value } }) => ({
      loginValues: R.assoc(id, value, state.loginValues),
      error: null
    }),
    updateSelectedAccountId: (state, { payload: selectedAccountId }) => {
      const account = selectedAccountId
        ? R.find(a => a.id === selectedAccountId)(state.eventUserGroups)
        : null;
      const selectedContact =
        account && state.userEmail
          ? R.find(c =>
              c.email ? c.email.toLowerCase().trim() === state.userEmail : false
            )(account.contacts)
          : null;

      return {
        selectedAccountId,
        selectedContactId: selectedContact ? selectedContact.id : null,
        contactValues: selectedContact ? selectedContact.values : {},
        groupValues: selectedAccountId
          ? R.compose(
              R.prop("values"),
              R.find(a => a.id === selectedAccountId)
            )(state.eventUserGroups)
          : {}
      };
    },
    updateSelectedContactId: (state, { payload: selectedContactId }) => {
      return {
        selectedContactId,
        contactValues:
          selectedContactId && state.selectedAccountId
            ? R.compose(
                R.prop("values"),
                R.find(c => c.id === selectedContactId),
                R.propOr([], "contacts"),
                R.find(a => a.id === state.selectedAccountId)
              )(state.eventUserGroups)
            : {}
      };
    },
    addSpeaker: state => {
      return {
        speakers: [
          ...state.speakers,
          {
            id: Math.random(),
            is_new: true,
            is_self: false,
            is_external: false,
            emailError: "",
            contactValues: {},
            additionalContacts: []
          }
        ]
      };
    },
    removeSpeaker: state => {
      const speakers = state.speakers.filter(
        s => s.id !== state.selectedSpeakerId
      );
      return {
        speakers,
        selectedSpeakerId: speakers[0].id
      };
    },
    fetchSingleSignOnIdentityProviders: R.identity,
    singleSignOnIdentityProviders: R.identity,
    setSingleSignOnIdentityProviders: (state, { payload }) => ({
      singleSignOnIdentityProviders: payload
    })
  },
  namespace: NAMESPACE
});

const { actions, getters } = model;

export { actions, getters };

export default model;
